'use client';

import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _ArrowIosLeft, _span, _ArrowIosRight, _span2;
var _excluded = ["orientation", "opts", "setApi", "plugins", "className", "children"],
  _excluded2 = ["className", "containerClassName"],
  _excluded3 = ["className"],
  _excluded4 = ["className", "variant"],
  _excluded5 = ["className", "variant"],
  _excluded6 = ["className", "dotClassName"];
import { createContext, forwardRef, useCallback, useContext, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import ArrowIosLeft from '@crehana/compass.icons.outline/ArrowIosLeft';
import ArrowIosRight from '@crehana/compass.icons.outline/ArrowIosRight';
import { Button } from "../Button";
import { cn } from "../utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var CarouselContext = /*#__PURE__*/createContext(null);
function useCarousel() {
  var context = useContext(CarouselContext);
  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }
  return context;
}
var Carousel = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$orientation = _ref.orientation,
    orientation = _ref$orientation === void 0 ? 'horizontal' : _ref$orientation,
    opts = _ref.opts,
    setApi = _ref.setApi,
    plugins = _ref.plugins,
    className = _ref.className,
    children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useEmblaCarousel = useEmblaCarousel(_objectSpread(_objectSpread({}, opts), {}, {
      axis: orientation === 'horizontal' ? 'x' : 'y'
    }), plugins),
    _useEmblaCarousel2 = _slicedToArray(_useEmblaCarousel, 2),
    carouselRef = _useEmblaCarousel2[0],
    api = _useEmblaCarousel2[1];
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    canScrollPrev = _useState2[0],
    setCanScrollPrev = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    canScrollNext = _useState4[0],
    setCanScrollNext = _useState4[1];
  var onSelect = useCallback(function (api) {
    if (!api) {
      return;
    }
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);
  var scrollPrev = useCallback(function () {
    api === null || api === void 0 ? void 0 : api.scrollPrev();
  }, [api]);
  var scrollNext = useCallback(function () {
    api === null || api === void 0 ? void 0 : api.scrollNext();
  }, [api]);
  var handleKeyDown = useCallback(function (event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      scrollPrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      scrollNext();
    }
  }, [scrollPrev, scrollNext]);
  useEffect(function () {
    if (!api || !setApi) {
      return;
    }
    setApi(api);
  }, [api, setApi]);
  useEffect(function () {
    if (!api) {
      return;
    }
    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);
    return function () {
      api === null || api === void 0 ? void 0 : api.off('select', onSelect);
    };
  }, [api, onSelect]);
  return /*#__PURE__*/_jsx(CarouselContext.Provider, {
    value: {
      carouselRef: carouselRef,
      api: api,
      opts: opts,
      orientation: orientation || ((opts === null || opts === void 0 ? void 0 : opts.axis) === 'y' ? 'vertical' : 'horizontal'),
      scrollPrev: scrollPrev,
      scrollNext: scrollNext,
      canScrollPrev: canScrollPrev,
      canScrollNext: canScrollNext
    },
    children: /*#__PURE__*/_jsx("section", _objectSpread(_objectSpread({
      ref: ref,
      onKeyDownCapture: handleKeyDown,
      className: cn('tw-relative', className),
      "aria-roledescription": "carousel"
    }, props), {}, {
      children: children
    }))
  });
});
Carousel.displayName = 'Carousel';
var CarouselContent = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var className = _ref2.className,
    containerClassName = _ref2.containerClassName,
    props = _objectWithoutProperties(_ref2, _excluded2);
  var _useCarousel = useCarousel(),
    carouselRef = _useCarousel.carouselRef,
    orientation = _useCarousel.orientation;
  return /*#__PURE__*/_jsx("div", {
    ref: carouselRef,
    className: cn('tw-overflow-hidden', containerClassName),
    children: /*#__PURE__*/_jsx("div", _objectSpread({
      ref: ref,
      className: cn('tw-flex', orientation === 'horizontal' ? '-tw-ml-1.6' : '-tw-mt-1.6 tw-flex-col', className)
    }, props))
  });
});
CarouselContent.displayName = 'CarouselContent';
var CarouselItem = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var className = _ref3.className,
    props = _objectWithoutProperties(_ref3, _excluded3);
  var _useCarousel2 = useCarousel(),
    orientation = _useCarousel2.orientation;
  return /*#__PURE__*/_jsx("div", _objectSpread({
    ref: ref,
    role: "group",
    "aria-roledescription": "slide",
    className: cn('tw-min-w-0 tw-shrink-0 tw-grow-0 tw-basis-full', orientation === 'horizontal' ? 'tw-pl-1.6' : 'tw-pt-1.6', className)
  }, props));
});
CarouselItem.displayName = 'CarouselItem';
var CarouselPrevious = /*#__PURE__*/forwardRef(function (_ref4, ref) {
  var className = _ref4.className,
    _ref4$variant = _ref4.variant,
    variant = _ref4$variant === void 0 ? 'icon' : _ref4$variant,
    props = _objectWithoutProperties(_ref4, _excluded4);
  var _useCarousel3 = useCarousel(),
    orientation = _useCarousel3.orientation,
    scrollPrev = _useCarousel3.scrollPrev,
    canScrollPrev = _useCarousel3.canScrollPrev;
  return /*#__PURE__*/_jsxs(Button, _objectSpread(_objectSpread({
    ref: ref,
    variant: variant,
    className: cn('tw-absolute tw-h-4 tw-w-4 tw-flex tw-items-center tw-justify-center tw-rounded-full !tw-p-0', orientation === 'horizontal' ? '-tw-left-2 tw-top-[50%] -tw-translate-y-[50%]' : '-tw-top-2 tw-left-[50%] -tw-translate-x-[50%] tw-rotate-90', !canScrollPrev && 'tw-hidden', className),
    disabled: !canScrollPrev,
    onClick: scrollPrev
  }, props), {}, {
    children: [_ArrowIosLeft || (_ArrowIosLeft = /*#__PURE__*/_jsx(ArrowIosLeft, {
      size: 24,
      className: "tw-fill-current"
    })), _span || (_span = /*#__PURE__*/_jsx("span", {
      className: "tw-sr-only",
      children: "Previous slide"
    }))]
  }));
});
CarouselPrevious.displayName = 'CarouselPrevious';
var CarouselNext = /*#__PURE__*/forwardRef(function (_ref5, ref) {
  var className = _ref5.className,
    _ref5$variant = _ref5.variant,
    variant = _ref5$variant === void 0 ? 'icon' : _ref5$variant,
    props = _objectWithoutProperties(_ref5, _excluded5);
  var _useCarousel4 = useCarousel(),
    orientation = _useCarousel4.orientation,
    scrollNext = _useCarousel4.scrollNext,
    canScrollNext = _useCarousel4.canScrollNext;
  return /*#__PURE__*/_jsxs(Button, _objectSpread(_objectSpread({
    ref: ref,
    variant: variant,
    className: cn('tw-absolute tw-h-4 tw-w-4 tw-flex tw-items-center tw-justify-center tw-rounded-full !tw-p-0', orientation === 'horizontal' ? '-tw-right-2 tw-tra tw-top-[50%] -tw-translate-y-[50%]' : '-tw-bottom-2 tw-left-[50%] -tw-translate-x-[50%] tw-rotate-90', !canScrollNext && 'tw-hidden', className),
    disabled: !canScrollNext,
    onClick: scrollNext
  }, props), {}, {
    children: [_ArrowIosRight || (_ArrowIosRight = /*#__PURE__*/_jsx(ArrowIosRight, {
      size: 24,
      className: "tw-fill-current"
    })), _span2 || (_span2 = /*#__PURE__*/_jsx("span", {
      className: "tw-sr-only",
      children: "Next slide"
    }))]
  }));
});
CarouselNext.displayName = 'CarouselNext';
var CarouselDots = /*#__PURE__*/forwardRef(function (_ref6, ref) {
  var className = _ref6.className,
    dotClassName = _ref6.dotClassName,
    props = _objectWithoutProperties(_ref6, _excluded6);
  var _useCarousel5 = useCarousel(),
    api = _useCarousel5.api;
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedIndex = _useState6[0],
    setSelectedIndex = _useState6[1];
  var _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    scrollSnaps = _useState8[0],
    setScrollSnaps = _useState8[1];
  var onSelect = useCallback(function () {
    if (!api) return;
    setSelectedIndex(api.selectedScrollSnap());
  }, [api]);
  var onReInit = useCallback(function () {
    if (!api) return;
    setScrollSnaps(api.scrollSnapList());
    setSelectedIndex(api.selectedScrollSnap());
  }, [api]);
  var scrollTo = useCallback(function (index) {
    if (!api) return;
    api.scrollTo(index);
  }, [api]);
  useEffect(function () {
    if (!api) return;
    setScrollSnaps(api.scrollSnapList());
    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onReInit);
    return function () {
      api.off('select', onSelect);
      api.off('reInit', onReInit);
    };
  }, [api, onSelect, onReInit]);
  return /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({
    ref: ref,
    role: "tablist",
    className: cn('tw-flex tw-justify-center tw-gap-1 tw-mt-2', className)
  }, props), {}, {
    children: scrollSnaps.length > 1 && scrollSnaps.map(function (snap, index) {
      return /*#__PURE__*/_jsx("button", {
        type: "button",
        role: "tab",
        onClick: function onClick() {
          return scrollTo(index);
        },
        "aria-selected": index === selectedIndex,
        className: cn('sm:tw-w-2 sm:tw-h-1 tw-w-1.6 tw-h-0.8 tw-rounded-full tw-transition-colors tw-cursor-pointer', index === selectedIndex ? 'tw-bg-primary-light-700 hover:tw-bg-primary-light-500' : 'tw-bg-neutral-light-500 hover:tw-bg-neutral-light-600', dotClassName),
        "aria-label": "Go to slide ".concat(index + 1)
      }, "carousel-dot-".concat(index, "-").concat(snap));
    })
  }));
});
CarouselDots.displayName = 'CarouselDots';
export { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, CarouselDots, useCarousel };